import lightGallery from 'lightgallery';
import { LightGallery } from 'lightgallery/lightgallery';
import lgThumbnail from 'lightgallery/plugins/thumbnail';
import lgZoom from 'lightgallery/plugins/zoom';
import GalleryItemDto from './GalleryItemDto';
import GalleryItems from './GalleryItems';

/**
 * Работа с плагином галереи.
 * Задача Gallery заключается в том, чтобы собрать подходящие элементы для отображения, отрендерить необхомый плагину
 * макет верстки и запустить плагин на этом макете, а также проксировать некоторые действия через доступный api,
 * например открыть/закрыть галерею
 * 
 * Открытие галереи по умолчанию происходит при клике по любому из дочерних элементов sourceNode, содержащему
 * атрибут 'data-gallery-open'. Значением атрибута может служить номер изображения, которое будет открыто.
 * 
 * @url https://www.npmjs.com/package/lightgallery
 */
export default class Gallery
{
    protected name: string;
    protected sourceNode: Element; // отсюда будем забирать картинки
    protected root: HTMLElement = null;
    protected items: GalleryItems;
    protected gallery: LightGallery;
    protected classes = {
        root: 'gallery',
        item: 'gallery__item',
        thumbnail: 'gallery__thumbnail',
    };

    public constructor(name: string, source: Element)
    {
        this.name = name;
        this.sourceNode = source;
        this.items = new GalleryItems;

        this.loadItems();
        this.appendOpenByClickHandler();
    }

    public setItems(items: GalleryItems)
    {
        if (this.gallery) {
            console.error('Gallery already loaded');
            return;
        }

        this.items = items;
    }

    public show(idx: number = 0)
    {
        if (!this.root) {
            this.render();
        }

        if (!this.gallery) {
            this.initGallery();
        }

        this.gallery.openGallery(idx < 0 ? 0 : idx);
    }

    public getIdx(imagePath: string): number|null
    {
        return this.items.getIdx(imagePath);
    }

    private initGallery()
    {
        this.gallery = lightGallery(this.root, {
            selector: '.' + this.classes.item,
            download: false,
            showCloseIcon: true,
            closeOnTap: true,
            mobileSettings: {
                showCloseIcon: true,
                closeOnTap: true,
            },
            plugins: [lgZoom, lgThumbnail],
        });

        this.appendGalleryCustomEvents();
    }

    //RAZ-747 - необходимо скрывать значок увеличения при открытии галереи, если размеры картинки меньше разрешения экрана
    private appendGalleryCustomEvents()
    {
        let gallery = document.getElementById('gallery-product-card-gallery');
        let items = this.items.export();

        gallery.addEventListener('lgBeforeSlide', (event: CustomEvent) => {
            let {index, prevIndex} = event.detail;
            let image = document.querySelector<HTMLImageElement>("img[src$='" + items[index]['url'] + "']") ;
   
            if (image) {
                this.hideInterfaceElement(image);
            }
        });

        gallery.addEventListener('lgAfterOpen', () => {
            let image = document.querySelector<HTMLImageElement>("img[src$='" + items[0]['url'] + "']") ;

            if (image) {
                this.hideInterfaceElement(image);
            }
        });
    }

    protected hideInterfaceElement(image: HTMLImageElement) 
    {
        if (image.naturalWidth < window.screen.width && image.naturalHeight < window.screen.height) {
            document.getElementById('lg-actual-size-1').classList.add('display_none');
        } else {
            document.getElementById('lg-actual-size-1').classList.remove('display_none');
        }
    }

    private appendOpenByClickHandler()
    {
        this.sourceNode.addEventListener('click', e => {
            let target = e.target as HTMLElement;

            if (target.getAttribute('data-gallery-open')) {
                let idx = Number(target.getAttribute('data-gallery-open'));

                this.show(isNaN(idx) ? 0 : idx);
            }
        });
    }

    private render()
    {
        this.root = document.createElement('div');
        this.root.classList.add(this.classes.root, 'display_none');
        this.root.id = 'gallery-' + this.name;

        this.renderItems();

        document.body.appendChild(this.root);
    }

    protected loadItems()
    {
        this.sourceNode.querySelectorAll('[data-gallery="' + this.name + '"]').forEach(node => {
            let imagePath = this.extractImagePath(node);
            let thumbnailPath = imagePath;

            if (!imagePath) {
                return;
            }

            if (node.childElementCount === 1) {
                thumbnailPath = this.extractImagePath(node.children.item(0));
            }

            if (!this.items.isExist(imagePath)) {
                this.items.push(new GalleryItemDto(imagePath, thumbnailPath));
            }
        });
    }

    private extractImagePath(element: Element): string
    {
        let image = '';

        if (element.getAttribute('data-src')) {
            image = element.getAttribute('data-src');
        } else if (element.tagName === 'A') {
            image = element.getAttribute('href');
        } else if (element.tagName === 'IMG') {
            image = element.getAttribute('src');
        }

        return image;
    }

    protected renderItems()
    {
        this.items.export().forEach(data => {
            let item = document.createElement('div');
            item.classList.add(this.classes.item);
            item.dataset.src = data.url;

            if (data.thumbnail !== '') {
                let thumbnail = document.createElement('img');
                thumbnail.src = data.thumbnail;

                item.appendChild(thumbnail);
            }

            this.root.appendChild(item);
        });
    }
}
